import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import isEqual from 'lodash/isEqual';

import {
  PageTitle,
  FilterButton,
  ExportButton,
  PaginationGroup,
  LimitPaginationButton,
  EmptyState,
  TableTitle,
  SaveFilterBlock,
  TableColumnSetting,
  Loading,
  AdditionalModal
} from 'components/common/Elements/Elements';
import InputSearch from 'components/common/forms/InputSearch/InputSearch';
import { useRegionPersistedSearch } from 'contexts/SearchPersistenceContext';
import CancellationsTable from './parts/CancellationsTable/CancellationsTable';
import Charts from './parts/Charts/Charts';
import FiltersBlock from './parts/FiltersBlock/FiltersBlock';

import { colorsNew } from 'components/common/charts/helpers/config.js';
import {
  exportConfigCancellations,
  mainTableConf,
  defaultFilters,
  defaultNavigation
} from './helpers/configurates';
import { getRequest, postRequest } from 'utils/requests';
import {
  replaceHistory,
  paddingForMenuSettings,
  checkingForExistenceOfOptions,
  parsConfigForHistory,
  reParsConfigForHistory,
  clearOrDefaultFilters
} from 'utils/common';
import { createReqBody } from './helpers/helpers';
import { validationNumber } from 'utils/validation';

import classes from './Cancellations.module.scss';
import { access, hasAccess, resources } from 'utils/permissions';

const Cancellations = () => {
  //   filters and pagination
  const [filter, setFilter] = useState({
    ...defaultFilters,
    ...defaultNavigation
  });

  const [filterBackend, setFilterBackend] = useState({
    key: 'cancFilters',
    value: []
  });
  const [visibleFilterSave, setVisibleFilterSave] = useState(false);
  const [visibleFilterEdit, setVisibleFilterEdit] = useState(false);
  const [changeFilter, setChangeFilter] = useState('Default');
  const [triggerResetDatePicker, setTriggerResetDatePicker] = useState(false);

  const [filterButton, setFilterButton] = useState(false);
  const [tableConf, setTableConf] = useState(
    JSON.parse(JSON.stringify(mainTableConf))
  );

  //data from api
  const [data, setData] = useState({});
  const [colorForReason, setColorForReason] = useState({});
  const [filters, setFilters] = useState({});

  //other data
  const [loader, setLoader] = useState(true);
  const [loaderTable, setLoaderTable] = useState(false);
  const [dataConnection, setDataConnection] = useState(false);
  const [selectLoader, setSelectLoader] = useState(false);

  // if you touch this trigger, then a request for filter lists is sent
  const [startFilter, setStartFilter] = useState(false);
  // status for comparison whether new options have been selected
  const [prevStateFilter, setPrevStateFilter] = useState();
  const [prevStateTableConf, setPrevStateTableConf] = useState();

  const [triggerSaveTableSettings, setTriggerSaveTableSettings] =
    useState(false);
  const [triggerStartEffect, setTriggerStartEffect] = useState(false);
  // const [enabledRequest, setEnabledRequest] = useState(true);

  const [isOpenAdditional, setIsOpenAdditional] = useState(false);
  const [infoForAdditionalModal, setInfoForAdditionalModal] = useState({});

  const { t } = useTranslation(['activity']);

  const history = useHistory();
  const query = new URLSearchParams(history.location.search);

  const tableHeight = useRef();
  const unmount = useRef(false);

  const onOpenAdditionalModal = info => {
    setInfoForAdditionalModal(info);
    setIsOpenAdditional(true);
  };

  const handleFilterInput = e => {
    setFilter({ ...filter, search: e.target.value, currentPage: 1 });
    setStartFilter(!startFilter);
  };

  // Keep the typed search when switching DR region (P81-132768).
  useRegionPersistedSearch(filter.search, value =>
    handleFilterInput({ target: { value } })
  );

  const handleFromTo = e => {
    const target = e.currentTarget;
    const name = target.getAttribute('field');
    if (target.id === name) {
      setFilter({
        ...filter,
        [name]: {
          ...filter[name],
          [`from`]: '',
          [`to`]: ''
        }
      });
      setStartFilter(!startFilter);
    } else {
      if (!validationNumber(target.value)) {
        setFilter({
          ...filter,
          [name]: {
            ...filter[name],
            [target.name]: target.value
          }
        });
        setStartFilter(!startFilter);
      }
    }
  };

  const handleFilterButton = () => {
    setFilterButton(!filterButton);
  };

  const handleLimitChange = limit => {
    if (filter.itemsPerPage === limit) {
      return;
    }
    setFilter({ ...filter, itemsPerPage: limit, currentPage: 1 });
    setStartFilter(!startFilter);
  };

  const handleSortable = field => () => {
    if (field === filter.fieldName) {
      const directionTemp = filter.direction * -1;
      setFilter({ ...filter, direction: directionTemp });
      setStartFilter(!startFilter);
    } else {
      setFilter({ ...filter, fieldName: field });
      setStartFilter(!startFilter);
    }
  };

  const changeCurrentPage = e => {
    setFilter({ ...filter, currentPage: e });
    setStartFilter(!startFilter);
  };

  // a function to check if the saved filters have a given combination of
  // the selected options and if so, switch to the saved filter

  const onClearSearch = () => {
    setFilter({ ...filter, search: '' });
    setStartFilter(!startFilter);
  };

  useEffect(() => {
    return () => {
      unmount.current = true;
    };
  }, []);

  const getFilterOptionsFromBackend = listFilter => {
    setSelectLoader(true);
    getRequest('/cs/cancellation/list/filters')
      .then(res => {
        if (!unmount.current) {
          const { body } = res;
          setFilters({ ...filters, ...body });
          // we are checking or in the new filter lists there are
          // parameters that are selected, and those that are not, turn off the selection
          const newCheckingList = checkingForExistenceOfOptions(
            listFilter,
            setFilter,
            body
          );
          newCheckingList && setPrevStateFilter(newCheckingList);
          newCheckingList &&
            replaceHistory(
              filter,
              undefined,
              history,
              'validation',
              newCheckingList,
              changeFilter,
              parsConfigForHistory(tableConf)
            );
          setSelectLoader(false);
        }
      })
      .catch(err => {
        !unmount.current && console.log(err);
      });
  };

  useEffect(() => {
    //after the select is closed, we get into this function.
    //We check or after closing the filter whether there have
    //been changes in the selected options and if so, we make a request
    if (triggerStartEffect) {
      if (
        (!isEqual(prevStateFilter, filter) &&
          JSON.parse(query.get('filter')) !== null) ||
        !isEqual(
          parsConfigForHistory(prevStateTableConf),
          parsConfigForHistory(tableConf)
        )
      ) {
        replaceHistory(
          filter,
          // setEnabledRequest,
          undefined,
          history,
          false,
          false,
          changeFilter,
          parsConfigForHistory(tableConf)
        );
      }
    }
    setTriggerStartEffect(true);
    // eslint-disable-next-line
  }, [startFilter, tableConf]);

  useEffect(() => {
    setLoaderTable(true);
    if (
      query.get('filter') === null &&
      isEqual(filter, {
        ...defaultFilters,
        ...defaultNavigation
      })
    ) {
      replaceHistory(
        filter,
        // setEnabledRequest,
        undefined,
        history,
        'replace',
        false,
        'Default',
        parsConfigForHistory(tableConf)
      );
      setVisibleFilterEdit(false);
      setVisibleFilterSave(false);
      setPrevStateFilter(filter);
      setPrevStateTableConf(tableConf);
      requestForFilteredData();
    } else if (
      query.get('filter') === null &&
      !isEqual(filter, {
        ...defaultFilters,
        ...defaultNavigation
      })
    ) {
      resetAllfilters(true);
      replaceHistory(
        filter,
        // setEnabledRequest,
        undefined,
        history,
        'replace',
        {
          ...defaultFilters,
          ...defaultNavigation
        },
        'Default',
        tableConf
      );
      setVisibleFilterEdit(false);
      setVisibleFilterSave(false);
    } else {
      setFilter(JSON.parse(query.get('filter')));
      setPrevStateFilter(JSON.parse(query.get('filter')));
      setPrevStateTableConf(
        reParsConfigForHistory(JSON.parse(query.get('tableConf')))
      );
      requestForFilteredData(JSON.parse(query.get('filter')));
      setChangeFilter(query.get('changeFilter'));
      setTableConf(
        reParsConfigForHistory(
          JSON.parse(query.get('tableConf')),
          mainTableConf
        )
      );
    }
    setTriggerResetDatePicker(!triggerResetDatePicker);
    // eslint-disable-next-line
  }, [query.get('filter') || query.get('jarvisRegion')]);

  const colorResonCreate = reasonInfo => {
    const dataColor = {};
    reasonInfo.forEach((el, index) => (dataColor[el.name] = colorsNew[index]));
    return dataColor;
  };

  const requestForFilteredData = (listFilter = filter) => {
    const linkForRequest = `/cs/cancellation/list?page=${listFilter.currentPage}&limit=${listFilter.itemsPerPage}`;
    const REQ_BODY = createReqBody(listFilter);

    postRequest(linkForRequest, REQ_BODY)
      .then(res => {
        if (!unmount.current) {
          setData(res.body);
          setColorForReason(
            colorResonCreate(res.body.chartData.cancellationReason)
          );
          setDataConnection(true);
          setLoader(false);
          setLoaderTable(false);
          getFilterOptionsFromBackend(listFilter);
        }
      })
      .catch(err => {
        if (!unmount.current) {
          setDataConnection(false);
          setLoader(false);
          setLoaderTable(false);
          console.log(err);
        }
      });
  };

  const resetAllfilters = (
    reload,
    onlyFilterLabel,
    onClickDefault,
    modeClearAll
  ) => {
    const newValueFilters = clearOrDefaultFilters(modeClearAll, defaultFilters);

    setFilter({ ...newValueFilters, ...defaultNavigation });

    if ((changeFilter === 'Default' || onClickDefault) && !modeClearAll) {
      setVisibleFilterSave(false);
      setVisibleFilterEdit(false);
      setTableConf(JSON.parse(JSON.stringify(mainTableConf)));
    } else {
      setVisibleFilterSave('button');
      if (changeFilter !== 'Default') {
        setVisibleFilterEdit(true);
      }
    }
    if (!onlyFilterLabel) {
      setChangeFilter('Default');
      setTableConf(JSON.parse(JSON.stringify(mainTableConf)));
    }

    setTriggerResetDatePicker(!triggerResetDatePicker);
    if (!reload) {
      setStartFilter(!startFilter);
    }
  };

  if (loader) {
    return <Loading />;
  }

  if (!dataConnection) {
    return <EmptyState message="connect" />;
  }

  return (
    <>
      <AdditionalModal
        isOpen={isOpenAdditional}
        setIsOpen={setIsOpenAdditional}
        info={infoForAdditionalModal}
      />
      <PageTitle title={t('activity:cancellation.CANCELLATIONS')} />
      <div className={classes.filters_main}>
        <InputSearch
          placeholder={t('activity:filters.SEARCH_AREA')}
          value={filter.search}
          onChange={handleFilterInput}
          onClearSearch={onClearSearch}
          className={classes.filter}
          disabled={loaderTable}
        />
        <FilterButton active={filterButton} onClick={handleFilterButton} />
        <SaveFilterBlock
          setFilterButton={setFilterButton}
          visibleFilterSave={visibleFilterSave}
          setVisibleFilterSave={setVisibleFilterSave}
          visibleFilterEdit={visibleFilterEdit}
          setVisibleFilterEdit={setVisibleFilterEdit}
          filter={filter}
          setFilter={setFilter}
          filterBackend={filterBackend}
          setFilterBackend={setFilterBackend}
          changeFilter={changeFilter}
          setChangeFilter={setChangeFilter}
          resetAllfilters={resetAllfilters}
          defaultNavigation={defaultNavigation}
          trigerResetDatePicker={triggerResetDatePicker}
          setTrigerResetDatePicker={setTriggerResetDatePicker}
          startFilter={startFilter}
          setStartFilter={setStartFilter}
          defaultFilters={defaultFilters}
          tableConf={tableConf}
          defaulTableConf={mainTableConf}
          setTableConf={setTableConf}
          triggerSaveTableSettings={triggerSaveTableSettings}
        />
      </div>
      <FiltersBlock
        filter={filter}
        setFilter={setFilter}
        startFilter={startFilter}
        setStartFilter={setStartFilter}
        filters={filters}
        filterButton={filterButton}
        trigerResetDatePicker={triggerResetDatePicker}
        resetAllfilters={resetAllfilters}
        selectLoader={selectLoader}
        setVisibleFilterSave={setVisibleFilterSave}
        setVisibleFilterEdit={setVisibleFilterEdit}
        changeFilter={changeFilter}
      />
      <Charts data={data.chartData} />
      <div className={classes.actions}>
        <TableTitle
          title={t('activity:cancellation.CANCELLATIONS')}
          additional={data.cancellationReqCount}
        />
        <div className={classes.actions_right}>
          <TableColumnSetting
            setFilterButton={setFilterButton}
            tableConf={tableConf}
            setTableConf={setTableConf}
            setVisibleFilterSave={setVisibleFilterSave}
            setVisibleFilterEdit={setVisibleFilterEdit}
            changeFilter={changeFilter}
            visibleFilterSave={visibleFilterSave}
            visibleFilterEdit={visibleFilterEdit}
            setTriggerSaveTableSettings={setTriggerSaveTableSettings}
            triggerSaveTableSettings={triggerSaveTableSettings}
            defaulTableConf={mainTableConf}
          />
          {hasAccess([resources.CANCELLATIONS_REQUEST, access.EXPORT]) && (
            <ExportButton
              exportConfig={exportConfigCancellations}
              changeOptions={tableConf}
              isDisabled={!data.cancellationReqCount}
            />
          )}
          <LimitPaginationButton
            currentLimit={filter.itemsPerPage}
            onLimitChange={handleLimitChange}
          />
        </div>
      </div>
      <div style={paddingForMenuSettings(tableConf, tableHeight)}>
        <div ref={tableHeight}>
          <CancellationsTable
            data={data.tableData}
            handleSortable={handleSortable}
            loader={loaderTable}
            tableConf={tableConf}
            filter={filter}
            handleFromTo={handleFromTo}
            onOpenAdditionalModal={onOpenAdditionalModal}
            colorForReason={colorForReason}
          />
        </div>
        {data.tableData && loaderTable === false && data.totalPages > 1 && (
          <PaginationGroup
            pageCount={data.totalPages}
            setPage={changeCurrentPage}
            currentPage={filter.currentPage}
            itemsTotal={data.cancellationReqCount}
            itemsPerPage={filter.itemsPerPage}
          />
        )}
      </div>
    </>
  );
};

export default Cancellations;
